import type {
  QueryDocumentSnapshot,
  FirestoreDataConverter,
} from 'firebase/firestore'
import {
  collection,
  doc,
  runTransaction,
  serverTimestamp,
} from 'firebase/firestore'
import { buildUnsupportedConverter } from '../shared/noops'
import { PromptType, PromptVersionStatus } from './types'
import type {
  FirestorePostRoomStatePromptVersion,
  FirestoreInteractiveElementPromptVersion,
  FirestoreRubricPromptVersion,
} from './schema'
import {
  interactiveElementPromptVersionSchema,
  postRoomStatePromptVersionSchema,
  rubricPromptVersionSchema,
} from './schema'
import type { FirebaseRepository } from '../../models/FirebaseRepository'
import {
  InteractiveElementPromptVersion,
  PostRoomStatePromptVersion,
  RubricPromptVersion,
} from '../../models/PromptVersion'
import type { StreamInterface } from 'tricklejs/dist/types'
import { type RubricType } from '../PromptRubricType'
import { type InteractiveElementPromptType } from '../PromptInteractiveElementType/types'
import { type PostRoomStatePromptType } from '../PromptPostRoomStateType/types'
import { modelItemStream, modelListStream } from '../../firestore-mobx/stream'
import { updateDocWithError } from '../../firestore-mobx/fetch'

export * from './schema'

const rubricReadConverter: FirestoreDataConverter<FirestoreRubricPromptVersion> =
  {
    toFirestore:
      buildUnsupportedConverter<FirestoreRubricPromptVersion>().toFirestore,
    fromFirestore: (snapshot: QueryDocumentSnapshot) => {
      const data = snapshot.data({ serverTimestamps: 'estimate' })
      return rubricPromptVersionSchema.parse(data)
    },
  }

const interactiveElementReadConverter: FirestoreDataConverter<FirestoreInteractiveElementPromptVersion> =
  {
    toFirestore:
      buildUnsupportedConverter<FirestoreInteractiveElementPromptVersion>()
        .toFirestore,
    fromFirestore: (snapshot: QueryDocumentSnapshot) => {
      const data = snapshot.data({ serverTimestamps: 'estimate' })
      return interactiveElementPromptVersionSchema.parse(data)
    },
  }

const postRoomStateReadConverter: FirestoreDataConverter<FirestorePostRoomStatePromptVersion> =
  {
    toFirestore:
      buildUnsupportedConverter<FirestorePostRoomStatePromptVersion>()
        .toFirestore,
    fromFirestore: (snapshot: QueryDocumentSnapshot) => {
      const data = snapshot.data({ serverTimestamps: 'estimate' })
      return postRoomStatePromptVersionSchema.parse(data)
    },
  }

const getColRef = (
  repository: FirebaseRepository,
  { promptType, subType }: LinkedTypeAndSubType
) => {
  return collection(
    repository.firestore,
    'prompt',
    promptType,
    'type',
    subType.toString(),
    'prompt_version'
  )
}

export const getPromptVersionDocRef = (
  repository: FirebaseRepository,
  params: Parameters<typeof getColRef>[1] & { versionId: string }
) => {
  return doc(getColRef(repository, params), params.versionId)
}

export const getPromptVersionConverter = (promptType: PromptType) => {
  switch (promptType) {
    case PromptType.rubric:
      return rubricReadConverter
    case PromptType.interactiveElement:
      return interactiveElementReadConverter
    case PromptType.postRoomState:
      return postRoomStateReadConverter
  }
}

const getDocRef = getPromptVersionDocRef
const getConverter = getPromptVersionConverter

export function getPromptVersions(
  repository: FirebaseRepository,
  {
    promptType,
    subType,
  }: { promptType: PromptType.rubric; subType: RubricType }
): StreamInterface<RubricPromptVersion[]>

export function getPromptVersions(
  repository: FirebaseRepository,
  {
    promptType,
    subType,
  }: {
    promptType: PromptType.interactiveElement
    subType: InteractiveElementPromptType
  }
): StreamInterface<InteractiveElementPromptVersion[]>

export function getPromptVersions(
  repository: FirebaseRepository,
  {
    promptType,
    subType,
  }: { promptType: PromptType.postRoomState; subType: PostRoomStatePromptType }
): StreamInterface<PostRoomStatePromptVersion[]>

export function getPromptVersions(
  repository: FirebaseRepository,
  {
    promptType,
    subType,
  }: {
    promptType: PromptType
    subType: RubricType | InteractiveElementPromptType | PostRoomStatePromptType
  }
) {
  const colRef = collection(
    repository.firestore,
    'prompt',
    promptType,
    'type',
    subType.toString(),
    'prompt_version'
  )

  switch (promptType) {
    case PromptType.rubric:
      return modelListStream(
        repository,
        colRef.withConverter(rubricReadConverter),
        RubricPromptVersion
      )
    case PromptType.interactiveElement:
      return modelListStream(
        repository,
        colRef.withConverter(interactiveElementReadConverter),
        InteractiveElementPromptVersion
      )
    case PromptType.postRoomState:
      return modelListStream(
        repository,
        colRef.withConverter(postRoomStateReadConverter),
        PostRoomStatePromptVersion
      )
  }
}

export function getPromptVersion(
  repository: FirebaseRepository,
  {
    versionId,
    promptType,
    subType,
  }: { versionId: string; promptType: PromptType.rubric; subType: RubricType }
): StreamInterface<RubricPromptVersion>

export function getPromptVersion(
  repository: FirebaseRepository,
  {
    versionId,
    promptType,
    subType,
  }: {
    versionId: string
    promptType: PromptType.interactiveElement
    subType: InteractiveElementPromptType
  }
): StreamInterface<InteractiveElementPromptVersion>

export function getPromptVersion(
  repository: FirebaseRepository,
  {
    versionId,
    promptType,
    subType,
  }: {
    versionId: string
    promptType: PromptType.postRoomState
    subType: PostRoomStatePromptType
  }
): StreamInterface<PostRoomStatePromptVersion>

export function getPromptVersion(
  repository: FirebaseRepository,
  {
    versionId,
    promptType,
    subType,
  }: {
    versionId: string
    promptType: PromptType
    subType: RubricType | InteractiveElementPromptType | PostRoomStatePromptType
  }
) {
  const colRef = collection(
    repository.firestore,
    'prompt',
    promptType,
    'type',
    subType.toString(),
    'prompt_version'
  )

  switch (promptType) {
    case PromptType.rubric:
      return modelItemStream(
        repository,
        doc(colRef.withConverter(rubricReadConverter), versionId),
        RubricPromptVersion
      )
    case PromptType.interactiveElement:
      return modelItemStream(
        repository,
        doc(colRef.withConverter(interactiveElementReadConverter), versionId),
        InteractiveElementPromptVersion
      )
    case PromptType.postRoomState:
      return modelItemStream(
        repository,
        doc(colRef.withConverter(postRoomStateReadConverter), versionId),
        PostRoomStatePromptVersion
      )
  }
}

export async function updatePromptVersionPrompt(
  repository: FirebaseRepository,
  params: Parameters<typeof getDocRef>[1] & {
    field: PromptKey
    newPrompt: string
  }
) {
  const converter = getConverter(params.promptType)
  const docRef = getDocRef(repository, params).withConverter(converter)

  const { newPrompt, field } = params

  await runTransaction(repository.firestore, async (tx) => {
    const docSnapshot = await tx.get(docRef)
    const data = docSnapshot.data()

    if (!docSnapshot.exists() || !data)
      throw new Error('Document does not exist')
    if (data.versionStatus !== PromptVersionStatus.draft)
      throw new Error('Document is not in draft status')

    return tx.update(docRef, {
      [field]: newPrompt || null,
    })
  })
}

export async function updatePromptVersionStatus(
  repository: FirebaseRepository,
  params: // require publish notes if moving status to published
  | (Parameters<typeof getDocRef>[1] & {
        status: PromptVersionStatus.published
        publisherNotes: string
      })
    | (Parameters<typeof getDocRef>[1] & {
        status: Exclude<PromptVersionStatus, 'published'>
        publisherNotes?: undefined
      })
) {
  const converter = getConverter(params.promptType)
  const docRef = getDocRef(repository, params).withConverter(converter)

  const { status } = params

  await runTransaction(repository.firestore, async (tx) => {
    const docSnapshot = await tx.get(docRef)
    const data = docSnapshot.data()

    if (!docSnapshot.exists() || !data)
      throw new Error('Document does not exist')

    const currentStatus = data.versionStatus

    if (currentStatus > status)
      throw new Error(`Can not move status ${currentStatus} to ${status}`)

    if (status !== PromptVersionStatus.published)
      return tx.update(docRef, {
        versionStatus: status,
        updatedAt: serverTimestamp(),
      })

    return tx.update(docRef, {
      versionStatus: status,
      updatedAt: serverTimestamp(),
      publisherUserId: repository.uid,
      publisherNotes: params.publisherNotes,
      publishedDate: serverTimestamp(),
    })
  })
}

export async function updatePromptVersionConfidenceThreshold(
  repository: FirebaseRepository,
  params: Parameters<typeof getDocRef>[1] & {
    confidenceThreshold: number
  }
) {
  const docRef = getDocRef(repository, params).withConverter(null)
  await updateDocWithError(
    docRef,
    {
      confidenceThreshold: params.confidenceThreshold,
      updatedAt: serverTimestamp(),
    },
    'UpdatePromptVersionConfidenceThresholdError'
  )
}

export async function deletePromptVersion(
  repository: FirebaseRepository,
  params: Parameters<typeof getDocRef>[1]
) {
  const converter = getConverter(params.promptType)
  const docRef = getDocRef(repository, params).withConverter(converter)

  await runTransaction(repository.firestore, async (tx) => {
    const docSnapshot = await tx.get(docRef)
    const data = docSnapshot.data()

    if (!docSnapshot.exists() || !data)
      throw new Error('Document does not exist')

    if (data.versionStatus !== PromptVersionStatus.draft)
      throw new Error(
        `Can not delete prompt version unless it is in draft state`
      )

    return tx.delete(docRef)
  })
}

export async function copyPromptVersion(
  repository: FirebaseRepository,
  params: Parameters<typeof getDocRef>[1] & {
    versionName: string
    versionNotes: string
  }
): Promise<string> {
  const converter = getConverter(params.promptType)
  const colRef = getColRef(repository, params).withConverter(converter)

  const sourceDocRef = doc(colRef, params.versionId)
  const destinationDocRef = doc(colRef)

  await runTransaction(repository.firestore, async (tx) => {
    const docSnapshot = await tx.get(sourceDocRef)
    const data = docSnapshot.data()

    if (!docSnapshot.exists() || !data)
      throw new Error('Document to copy does not exist')

    if (params.versionName === data.versionName)
      throw new Error(
        `Version name must be different from the original version name`
      )

    const newData = {
      ...data,
      versionName: params.versionName,
      versionNotes: params.versionNotes,
      versionStatus: PromptVersionStatus.draft,
      updatedAt: serverTimestamp(),
      createdAt: serverTimestamp(),
      creatorUserId: repository.uid,
    }

    // remove all publish related fields
    for (const key in newData) {
      if (key.startsWith('publish')) delete newData[key as keyof typeof newData]
    }

    tx.set(destinationDocRef.withConverter(null), newData)
  })

  return destinationDocRef.id
}

export type PromptKey =
  | 'primaryPrompt'
  | 'statsPrompt'
  | 'reminderPrompt'
  | 'outputPrompt'

type LinkedTypeAndSubType =
  | {
      promptType: PromptType.rubric
      subType: RubricType
    }
  | {
      promptType: PromptType.interactiveElement
      subType: InteractiveElementPromptType
    }
  | {
      promptType: PromptType.postRoomState
      subType: PostRoomStatePromptType
    }
